// ══════════════════════════════════════════════════════════════════
//  낮 → 밤 계수 — 밤의 mods.js 자리.
//
//  ■ **밤과 정확히 같은 문법을 쓴다.** 훅 이름은 코드에 박고, 값·결합 규칙은
//    표 하나(DAY_EFFECTS)로 뺀다. 그래서 낮 3축을 리모델링해도 이 파일의
//    표만 갈아끼우면 되고, 밤은 안 바뀐다.
//
//  ■ **키 이름을 밤 mods.js 와 맞춘다.** 언젠가 밤이 `resolveMods()` 결과에
//    낮의 것을 합칠 때 이름이 같아야 한 줄로 끝난다. 다만 **별도 객체**로 넘긴다 —
//    합치는 규칙(가산인가 곱연산인가)이 정본 미기재이므로, **밤이 합치기 전까지는
//    아무 일도 안 일어난다.** 중립값 = 변화 0 이 그 안전장치다.
//
//  ■ **밤에 자리가 뚫렸다**(2026-07-29). `nightio.dayMods` 가 이걸 실어 보내고
//    `gb/dayin.js` 가 받는다. 구 주석은 *"계산만 하고 아무 데도 안 닿는다"* 였는데
//    **더 이상 사실이 아니다** — 17개 훅이 실제로 밤을 바꾼다.
// ══════════════════════════════════════════════════════════════════
import { SHARE_STAB_DROP_MUL } from './values.js';

// 중립값 — 낮이 아무것도 안 했을 때. **전부 1 또는 0이라 곱해도 더해도 변화가 없다.**
export function neutralDayMods() {
  return {
    dmgMul: 1,          // 무기 피해            ← 무기축 1·5단계
    rateMul: 1,         // 공격 속도            ← (구 지팡이축 — 지팡이 삭제로 발신자 없음. 키는 계약이라 남긴다)
    fireRateMul: 1,     // 연사                 ← 물맷돌축 3단계
    rangeMul: 1,        // 사거리               ← 물맷돌축 5단계
    radiusMul: 1,       // 호 반경              ← (구 지팡이축 — 위와 같다)
    stabRecMul: 1,      // 안정성 **회복**       ← 양축 1단계 · 소이벤트 ⑩ 소나기
    stabDropMul: 1,     // 안정성 **감소**       ← N24 나눔(−20% 완화)
    dogDps: 1,          // 개 물기               ← 개축 3단계
    dogSpeed: 1,        // 개 반응·이동          ← 개축 1·5단계
    dogDetect: 1,       // 개 감지               ← 개축 4단계 밤눈
    pieceMul: 1,        // 조각 획득             ← 소이벤트 ⑥ 무너진 우리
    downGuard: 0,       // 밤 1회 다운 무효      ← 개축 6단계
    freeCard: 0,        // 밤마다 무료 요령      ← 무기축 6단계 캡스톤
    stabStart: 100,     // 안정성 시작값         ← 전날 패닉이면 85
    priority: 'nearest',// 무기 타겟 우선순위     ← 물맷돌축 2·4·6단계
    // 【2026-08-06 상인 확정판 — 새 키 넷】 gb/dayin.js 와 키 계약. 값은 effects 표.
    maxHpMul: 1,        // 최대 HP               ← 볶은 곡식(자루=영구 / 한 줌=그 밤)
    speedMul: 1,        // 이동속도              ← 가죽 신
    dmgTakenMul: 1,     // 받는 피해             ← 가죽 앞치마
    waterHealMul: 1,    // 물웅덩이 한 모금      ← 무화과(뭉치=영구 / 몇 알=그 밤)
  };
}

// ══════════════════════════════════════════════════════════════════
//  상인 물건 → 훅 【사장님 확정 표 2026-08-06 · 수치는 전부 잠정 — 경제 마지막】
//    영구(owned 단계)와 그 밤 한정(s.day.tonight — 가짜의 낱개)을 여기서 푼다.
//    "진짜는 머무는 것을 팔고, 가짜는 머물지 않는 것을 판다."
// ══════════════════════════════════════════════════════════════════
export const GOODS_EFFECTS = {
  // 영구 — 단계당 얹는다(잠정값)
  //
  // 목줄 계수만은 잠정이 아니라 **실측**이다【2026-08-06 · probe_dog_tiers】.
  //   N8 · 240초 · 개 4마리 · 시드 11/42 평균, 개 킬수(T0 = 70):
  //     +0.15 → 70·96·96·128   (1→2단계 +0 — 한 단계가 소음에 먹힌다)
  //     +0.25 → 70·102·133·161 (+32/+31/+28 — 단조·또렷, 유일하게 계단이 고르다)
  //     +0.35 → 70·122·137·192 (+52/+15/+55 — 들쭉날쭉)
  //   치즈당 한계 킬(mul 기준): 목줄 8.0/4.4/2.3 > 개 3마리째 1.8 > 4마리째 1.05.
  //   → 목줄 먼저 채우고 개를 늘리는 순서가 서고, 개축 3단계(+0.20)와도 한 식구다.
  collarThick: { per: { dogDps: +0.25 } },
  figBundle:   { per: { waterHealMul: +0.15 } },
  grainSack:   { per: { maxHpMul: +0.08 } },
  shoes:       { per: { speedMul: +0.06 } },
  apron:       { per: { dmgTakenMul: -0.10 } },
  oilJar:      { add: { downGuard: 1 } },        // 매 밤 1회 기상(영구)
  // ── 일감 자리 편입분 【2026-08-07】 ─────────────────────────────
  //   구 낮 3축의 **살아 있던 훅 여섯**이 물건이 됐다. 값은 축의 단계값을 그대로
  //   옮긴 것이고(뎀 +10 / 연사 +10 · 사거리 +10 / 안정회복 +15 / 개 반응·감지),
  //   축이 6단계에 걸쳐 주던 것을 물건 2~3단계로 접었으므로 **단계당 몫은 더 크다.**
  //   ⚠ 잠정 — 목줄(실측)을 뺀 나머지와 같은 처지다.
  bell:        { per: { stabRecMul: +0.15 } },                    // 구 양축 1단계
  driedMeat:   { per: { dogSpeed: +0.15, dogDetect: +0.20 } },    // 구 개축 1·4·5단계
  slingCord:   { per: { dmgMul: +0.10 } },                        // 구 물맷돌축 1·5단계
  longCord:    { per: { fireRateMul: +0.10, rangeMul: +0.10 } },  // 구 물맷돌축 3·5단계
  // 그 밤 한정 — s.day.tonight 플래그(가짜의 낱개)
  _tonight: {
    grain: { maxHpMul: +0.08 },
    figs:  { waterHealMul: +0.15 },
    oil:   { downGuard: 1 },
    dogDpsUp: { dogDps: +0.25 },   // 낡은 목줄 = 진짜 1단계 하룻밤치 — 계수 동행【실측 주석 위】
  },
};

// ══════════════════════════════════════════════════════════════════
//  훅 → **사람이 읽는 한 줄** 【사장님 지시 2026-08-07】
//    *"정확하게 뭘 사면 뭘 준다 설명을 해줘야지. 애매하게 체력이 는다 내지는
//      뭐 이런거 말고, 개가 한마리 추가, 체력 얼마 추가, 한번 회생 (영구) 이런식으로."*
//
//  ■ **문구를 손으로 안 적는다.** 위 `GOODS_EFFECTS` 에서 **생성**한다 —
//    손으로 적으면 값을 고쳤을 때 화면이 옛 수를 계속 말한다(이 저장소가 여러 번
//    당한 「고친 값이 화면에 안 닿는다」의 반대 방향 사고다).
//  ■ 여기 없는 훅은 **일부러 없다** — `apply` 로 직접 상태를 바꾸는 물건(양·개·낱개)은
//    표에 효과가 없으므로 `goods.js` 의 `gives` 가 제 문장을 든다.
// ══════════════════════════════════════════════════════════════════
const pct = (v) => `${v > 0 ? '+' : '−'}${Math.round(Math.abs(v) * 100)}%`;
export const HOOK_KO = {
  dmgMul:       (v) => `물맷돌 피해 ${pct(v)}`,
  fireRateMul:  (v) => `연사 ${pct(v)}`,
  rangeMul:     (v) => `사거리 ${pct(v)}`,
  stabRecMul:   (v) => `안정성 회복 ${pct(v)}`,
  stabDropMul:  (v) => `안정성 감소 ${pct(v)}`,
  dogDps:       (v) => `개 공격력 ${pct(v)}`,
  dogSpeed:     (v) => `개 반응 ${pct(v)}`,
  dogDetect:    (v) => `개 감지 ${pct(v)}`,
  // ⚠ **화면에 나가는 이름은 「물웅덩이」다**(명세 4-2 — 「우물」은 코드 안의 말).
  //   상점이 산 것과 밤에서 본 것이 같은 이름이어야 플레이어가 둘을 잇는다.
  waterHealMul: (v) => `물웅덩이 회복량 ${pct(v)}`,
  maxHpMul:     (v) => `최대 체력 ${pct(v)}`,
  speedMul:     (v) => `이동 속도 ${pct(v)}`,
  dmgTakenMul:  (v) => `받는 피해 ${pct(v)}`,
  pieceMul:     (v) => `조각 획득 ${pct(v)}`,
  downGuard:    (n) => `쓰러져도 ${n}회 회생`,
};

// 그 물건이 **한 번 살 때 주는 것** — 표에서 그대로 뽑는다. 없으면 null.
export function effectLine(id) {
  const E = GOODS_EFFECTS[id];
  if (!E) return null;
  const parts = [];
  for (const [k, v] of Object.entries(E.per ?? {})) parts.push(HOOK_KO[k]?.(v) ?? `${k} ${v}`);
  for (const [k, v] of Object.entries(E.add ?? {})) parts.push(HOOK_KO[k]?.(v) ?? `${k} ${v}`);
  return parts.length ? parts.join(' · ') : null;
}

// ⚠⚠ **낮 3축 표(`DAY_EFFECTS`)가 여기 있었다** 【일감 자리 폐기 2026-08-07 —
//   사장님: *"일감자리 없애고 상인에 다 편입시켜"* · *"별자리 시스템 폐기라니까"*】.
//   `pasu:1..6` · `sheep:1..6` · `dog:1..6` 열여덟 칸이었고, `state.axes` 의 산 단계
//   수만큼 곱해서 얹었다. 축·별자리 게이트·일감 자리 오브젝트가 통째로 없어졌으므로
//   표도 같이 걷는다 — **살아 있던 훅 여섯은 위 `GOODS_EFFECTS` 로 옮겼고**, 죽어
//   있던 셋(freeCard·lead2·birthCare — 소비처가 한 곳도 없었다)은 옮기지 않았다.
//   되살릴 일이 생기면 이 주석과 git 이력이 설계를 들고 있다.

// 소이벤트 — 밤 몫만. 낮 몫(양 유입·치료비·정보)은 flock/dogs 가 이미 처리한다.
export const EVENT_MODS = {
  brokenPen: { pieceMul: +0.20 },                   // ⑥ 무너진 우리
  shower:    { stabRecMul: +0.10 },                 // ⑩ 소나기 — **다음 밤**
};

// 흉터의 밤 계수 자리. 현재 켜지는 흉터는 명단·정서 효과라 수치 훅이 없다.
export const SCAR_MODS = {};

// ── 해석 ─────────────────────────────────────────────────────────
export function resolveDayMods(state) {
  const m = neutralDayMods();
  const weaponKey = state.campaign.weapon;

  const apply = (tbl) => {
    if (!tbl) return;
    const addSet = new Set(tbl._add ?? []);
    for (const [k, v] of Object.entries(tbl)) {
      if (k === '_add') continue;
      if (addSet.has(k)) m[k] += v;              // 가산 훅(개 수·무료 카드·다운 무효)
      else if (typeof m[k] === 'number') m[k] *= (1 + v);   // 배율 훅
      else m[k] = v;                             // 문자열(priority)은 덮어쓴다
    }
  };

  // ⚠ 3축 순회가 여기 있었다 — 축 폐기(위 주석). 이제 계수는 **상인 물건**에서만 온다.

  // 식탁(N24) — **보상은 계수 하나다.** 나눈 그 밤만 안정성 감소가 완화된다.
  //   소이벤트가 아니라 고정 밤 사건이라 표를 안 타고 여기서 직접 건다.
  if (state.day.table?.shared) m.stabDropMul *= SHARE_STAB_DROP_MUL;

  // 소이벤트 · 흉터
  apply(EVENT_MODS[state.day.event]);
  for (const sc of state.scars) apply(SCAR_MODS[sc.id]);

  // ── 상인 물건 【2026-08-06】 — 영구(owned)와 그 밤(tonight)을 얹는다 ──
  {
    const owned = state.goods?.owned ?? {};
    for (const [id, eff] of Object.entries(GOODS_EFFECTS)) {
      if (id === '_tonight') continue;
      const t = owned[id] ?? 0;
      if (t <= 0) continue;
      if (eff.per) for (const [k, v] of Object.entries(eff.per)) m[k] *= (1 + v * t);
      if (eff.add) for (const [k, v] of Object.entries(eff.add)) m[k] += v;
    }
    const tn = state.day?.tonight ?? {};
    for (const [flag, tbl] of Object.entries(GOODS_EFFECTS._tonight)) {
      if (!tn[flag]) continue;
      for (const [k, v] of Object.entries(tbl)) {
        if (k === 'downGuard') m[k] += v; else m[k] *= (1 + v);
      }
    }
  }

  // 우선순위는 낮 설정이 정본이다(해금이 settings.priority 를 이미 갱신했다)
  m.priority = state.settings.priority;
  // 안정성 시작값 — 전날 패닉이면 85(명세 5장). 밤에 넘길 자리는 아직 없다.
  m.stabStart = state.flock.nextStabStart;
  return m;
}
